/**
 * ResultsStickyFiltersBar Component
 *
 * Displays the sticky filters bar with:
 * - AI explainer or loading state
 * - Filter pills (active filters, suggested filters, brands)
 * - Mobile "All Filters" button
 */

import React from "react";

import { ChevronRight, Sparkles } from "lucide-react";

import type { FilterPill } from "@/components/results/FilterSection";
import { FiltersSectionLoading } from "@/components/results/FiltersSectionLoading";
import { MobileAllFiltersButton } from "@/components/results/MobileAllFiltersButton";
import { ResultsFacetContainer } from "@/components/results/ResultsFacetContainer";
import { colors } from "@/styles/design-tokens";

/** Props for the mobile Suggestions button */
interface MobileSuggestionsButtonProps {
  onClick: () => void;
  suggestionsCount: number;
  isExpanded: boolean;
  className?: string;
}

/** Compact mobile "Suggestions" button with AI sparkle icon */
function MobileSuggestionsButton({
  onClick,
  suggestionsCount,
  isExpanded,
  className = "",
}: MobileSuggestionsButtonProps): React.ReactElement {
  return (
    <button
      onClick={onClick}
      aria-expanded={isExpanded}
      className={`inline-flex items-center gap-1.5 py-2 text-sm font-medium text-ink lg:hidden ${className}`}
    >
      <Sparkles className="h-3.5 w-3.5" />
      <span className="leading-none">
        Suggestions {suggestionsCount > 0 && `(${suggestionsCount})`}
      </span>
      <ChevronRight className={`h-4 w-4 transition-transform ${isExpanded ? "rotate-90" : ""}`} />
    </button>
  );
}

export interface ResultsStickyFiltersBarProps {
  /** Whether search is in progress */
  isSearching: boolean;
  /** Whether assistant is loading */
  isLoadingAssistant: boolean;
  /** Whether filters are expanded (desktop) */
  filtersExpanded: boolean;
  /** Callback to change expanded state (desktop) */
  setFiltersExpanded: React.Dispatch<React.SetStateAction<boolean>>;
  /** Whether mobile suggestions are expanded */
  mobileSuggestionsExpanded: boolean;
  /** Callback to change mobile suggestions expanded state */
  setMobileSuggestionsExpanded: React.Dispatch<React.SetStateAction<boolean>>;
  /** Whether the bar is currently sticky */
  isSticky: boolean;
  /** Height of the filters content (for placeholder) */
  filtersHeight: number;
  /** Ref for the section that triggers sticky behavior */
  filtersSectionTopRef: React.RefObject<HTMLDivElement | null>;
  /** Ref for the filters content */
  filtersContentRef: React.RefObject<HTMLDivElement | null>;
  /** Active filters to display */
  activeFilters: FilterPill[];
  /** Suggested filters from AI */
  suggestedFilters: FilterPill[];
  /** Selected brand pills */
  selectedBrands: FilterPill[];
  /** Similar to brands pills */
  similarToBrandsPills: FilterPill[];
  /** Total active brands count */
  activeBrandsCount: number;
  /** AI justification text */
  assistantJustification: string | null | undefined;
  /** Count of applied filters (for badge) */
  appliedFiltersCount: number;
  /** Callback when mobile filters button is clicked */
  onMobileFiltersClick: () => void;
  /** Callback to clear active attribute pills in the suggestions section */
  onClearSuggestedFilters: () => void;
  /** Callback to clear active brands in the suggestions section */
  onClearSuggestedBrands: () => void;
}

/**
 * Sticky filters bar with AI explainer and filter pills
 */
export function ResultsStickyFiltersBar({
  isSearching,
  isLoadingAssistant,
  filtersExpanded,
  setFiltersExpanded,
  mobileSuggestionsExpanded,
  setMobileSuggestionsExpanded,
  isSticky,
  filtersHeight,
  filtersSectionTopRef,
  filtersContentRef,
  activeFilters,
  suggestedFilters,
  selectedBrands,
  similarToBrandsPills,
  activeBrandsCount,
  assistantJustification,
  appliedFiltersCount,
  onMobileFiltersClick,
  onClearSuggestedFilters,
  onClearSuggestedBrands,
}: ResultsStickyFiltersBarProps): React.ReactElement {
  // Calculate total suggestions count for the mobile button
  const totalSuggestionsCount = React.useMemo(() => {
    const suggestedBrandsCount = selectedBrands.filter((b) => b.variant !== "active").length;
    const suggestedSimilarCount = similarToBrandsPills.filter((b) => b.variant !== "active").length;
    return suggestedFilters.length + suggestedBrandsCount + suggestedSimilarCount;
  }, [selectedBrands, similarToBrandsPills, suggestedFilters.length]);

  // Check if AI has any recommendations available
  const hasSuggestions = totalSuggestionsCount > 0 || isLoadingAssistant;

  // Shared props for ResultsFacetContainer (DRY)
  const facetContainerProps = {
    activeFilters,
    suggestedFilters,
    selectedBrands,
    similarToBrands: similarToBrandsPills,
    totalFiltersCount: activeFilters.length,
    totalBrandsCount: activeBrandsCount,
    isLoadingAssistant,
    onClearSuggestedFilters,
    onClearSuggestedBrands,
  };
  return (
    <div
      ref={filtersSectionTopRef}
      className="mt-4 w-full max-w-full lg:mt-5 lg:max-w-[1220px]"
      style={{
        height: isSticky ? `${filtersHeight}px` : "auto",
      }}
    >
      {/* Loading or AI Explainer Copy - Hidden on mobile (replaced by compact button) */}
      {(isSearching ||
        isLoadingAssistant ||
        (filtersExpanded && (activeFilters.length > 0 || assistantJustification))) && (
        <div
          className={`hidden lg:flex min-h-[32px] gap-2 py-1 lg:items-center lg:gap-[8px] lg:py-0 ${
            isSearching ? "items-center" : "items-start"
          }`}
        >
          {isSearching ? (
            <>
              <div className="h-2.5 w-2.5 rounded-full bg-ink" />
              <span className="text-title text-ink">Finding results...</span>
            </>
          ) : (
            <>
              <Sparkles className="mt-0.5 h-4 w-4 flex-shrink-0 lg:mt-0" />
              <span className="text-action text-ink-secondary">
                {isLoadingAssistant
                  ? "Getting AI recommendations..."
                  : assistantJustification ||
                    "Smart filter and brand suggestions based on your search."}
              </span>
            </>
          )}
        </div>
      )}

      {/* Selected Filters Container - Smooth sticky behavior */}
      <div
        ref={filtersContentRef}
        className={`w-full max-w-full ${
          isSticky ? "top-[48px] px-4 pb-2 pt-3 lg:top-[60px] lg:px-[30px] lg:pb-4 lg:pt-5" : ""
        }`}
        style={{
          marginTop: "0px",
          ...(isSticky
            ? {
                width: "100vw",
                position: "fixed",
                left: "0",
                right: "0",
                zIndex: 40,
                backgroundColor: colors.background.white,
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
              }
            : {}),
        }}
      >
        {/* Mobile button row - All Filters and Suggestions side by side, wraps on very small screens */}
        {!isSearching && (
          <div className="flex flex-wrap items-center gap-6 lg:hidden">
            <MobileAllFiltersButton
              onClick={onMobileFiltersClick}
              filterCount={appliedFiltersCount}
            />
            {hasSuggestions && (
              <MobileSuggestionsButton
                onClick={() => setMobileSuggestionsExpanded(!mobileSuggestionsExpanded)}
                suggestionsCount={totalSuggestionsCount}
                isExpanded={mobileSuggestionsExpanded}
              />
            )}
          </div>
        )}

        {/* Show loading component during active search only */}
        {isSearching ? (
          <FiltersSectionLoading />
        ) : (
          <>
            {/* Desktop: Always show ResultsFacetContainer */}
            <div className="hidden lg:block">
              <ResultsFacetContainer
                {...facetContainerProps}
                isExpanded={filtersExpanded}
                onExpandedChange={setFiltersExpanded}
              />
            </div>

            {/* Mobile: Show ResultsFacetContainer below buttons when expanded AND suggestions exist */}
            {mobileSuggestionsExpanded && hasSuggestions && (
              <div className="mt-3 lg:hidden">
                <ResultsFacetContainer
                  {...facetContainerProps}
                  isExpanded={true}
                  onExpandedChange={() => setMobileSuggestionsExpanded(false)}
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
